import { msalInstance, apiScopes } from '../auth/msalConfig';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
  }
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');

  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    ...options,
    headers: {
      // Only set a JSON content-type when there's actually a body. A DELETE with no
      // body + content-type: application/json makes Fastify reject it with
      // "Body cannot be empty when content-type is set to 'application/json'".
      ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers ?? {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetch a binary file (with the bearer token) and trigger a browser download.
 * The filename comes from the Content-Disposition header when present.
 */
export async function downloadFile(
  path: string,
  params?: Record<string, string>,
  fallbackName = 'download',
): Promise<void> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');
  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const response = await fetch(`${API_BASE_URL}/api/v1${path}${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? fallbackName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a binary file (with the bearer token) and open it inline in a new tab.
 * Used for private blobs (e.g. bank statements) that the browser cannot reach
 * directly — the API streams the bytes and we render them from an object URL.
 */
export async function openFileInNewTab(path: string, params?: Record<string, string>): Promise<void> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');
  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const response = await fetch(`${API_BASE_URL}/api/v1${path}${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }

  // response.blob() carries the Content-Type from the response, so the browser
  // renders PDFs/images inline.
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after the new tab has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Fetch a binary file (with the bearer token) and return an object URL for it.
 * Use for embedding a private/generated document in an <iframe> preview. The
 * caller is responsible for URL.revokeObjectURL() when the preview closes.
 */
export async function fetchBlobObjectUrl(path: string, params?: Record<string, string>): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');
  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  const response = await fetch(`${API_BASE_URL}/api/v1${path}${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, body);
  }
  return URL.createObjectURL(await response.blob());
}

/**
 * POST a body and receive a binary response, keeping the response headers.
 *
 * Built for check printing, where one call both mints the run and returns its
 * PDF: the bytes go straight to an object URL for the print dialog, while the
 * run id — which the UI needs in order to confirm or spoil the batch afterwards
 * — rides along in a header rather than forcing a second round trip.
 */
export async function postForBlob(
  path: string,
  body: unknown,
): Promise<{ objectUrl: string; headers: Headers }> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');
  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      // This POST assigns check numbers; a retry must not assign a second set.
      'Idempotency-Key': newIdempotencyKey(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new ApiError(response.status, err);
  }
  return { objectUrl: URL.createObjectURL(await response.blob()), headers: response.headers };
}

/**
 * POST a request and consume a newline-delimited JSON response as it arrives.
 *
 * The regular wrapper awaits the whole body, which is exactly what a streamed
 * chat reply must not do. Each complete line is parsed and handed to `onFrame`;
 * a trailing partial line is held until the next chunk completes it. Aborting
 * `signal` tears down the request, which the API treats as "stop".
 *
 * NDJSON rather than EventSource: the request needs an Entra bearer token and
 * EventSource cannot set headers.
 */
export async function streamNdjson<TFrame>(
  path: string,
  body: unknown,
  onFrame: (frame: TFrame) => void,
  signal?: AbortSignal,
): Promise<void> {
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) throw new Error('No authenticated account');
  const { accessToken } = await msalInstance.acquireTokenSilent({
    scopes: apiScopes,
    account: accounts[0],
  });

  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body ?? {}),
    signal,
  });

  if (!response.ok) {
    const errBody = await response.json().catch(() => null);
    throw new ApiError(response.status, errBody);
  }
  if (!response.body) throw new Error('Streaming is not supported by this browser');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flush = (chunk: string) => {
    buffer += chunk;
    let nl = buffer.indexOf('\n');
    while (nl !== -1) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line) {
        try {
          onFrame(JSON.parse(line) as TFrame);
        } catch {
          // A frame we can't parse is dropped rather than killing the stream.
        }
      }
      nl = buffer.indexOf('\n');
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    flush(decoder.decode(value, { stream: true }));
  }
  flush(decoder.decode());
  const tail = buffer.trim();
  if (tail) {
    try {
      onFrame(JSON.parse(tail) as TFrame);
    } catch {
      /* ignore a truncated final frame */
    }
  }
}

/**
 * A fresh key per logical write.
 *
 * Attached to POSTs so a retry of the SAME request — a proxy retry, a
 * double-fire, a resubmitted form — cannot post twice. A new page load makes a
 * new key by design: a person deliberately submitting the same payment again is
 * a different question, handled by the duplicate warnings in duplicateConfirm.
 */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export const api = {
  get: <T>(path: string, params?: Record<string, string>) =>
    apiRequest<T>(path + (params ? '?' + new URLSearchParams(params).toString() : '')),
  post: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'Idempotency-Key': newIdempotencyKey() },
    }),
  put: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};
